import { Grid } from '@mui/material';
import * as mapboxgl from 'mapbox-gl';
import React, { useMemo, createRef, useContext, useEffect, useRef, useState } from 'react';
import { FormContext } from '../../../context/Form.context';
import MapEstimate from './MapEstimate.component';
import '../../../styles/cssStyles.css';
import ReactDOM from 'react-dom';
import LocationPopUp from './LocationPopUp.component';
import 'mapbox-gl/dist/mapbox-gl.css';

mapboxgl.accessToken = process.env.MAPBOX_TOKEN_ONPREM;
mapboxgl.baseApiUrl = process.env.MAPBOX_BASE_API_URL;

/**
 * Map View component is responsible for displaying the map itself
 * as well as the estimate cards to its side
 * @param {*} props
 * @returns the component
 */
const MapView = (props) => {
    const {
        pageDisplayRange,
        setProvider,
        otherLocationsOpen,
        setOtherLocationsOpen,
        closestLocationIndex,
        setClosestLocationIndex,
        topLocation,
        setTopLocation,
        page,
        rowsPerPage,
        selectedProvider,
        setEstimate,
        sortEstimateWithProviders,
    } = props;
    const { allProvidersPool: basesAllProvidersPool, setSingleEstimateResponse } =
        useContext(FormContext);
    let allProvidersPool = JSON.parse(JSON.stringify(basesAllProvidersPool));
    let allSortedProviders = allProvidersPool;

    const mapContainer = useRef(null);
    const map = useRef(null);
    const [highlightedProvider, setHighlightedProvider] = useState('');
    const [currentMarkers, setCurrentMarkers] = useState([]);
    const markerRefs = useMemo(
        /**
         *
         * https://stackoverflow.com/questions/65350114/useref-for-element-in-loop-in-react
         * did this based on this post above, useMemo for performance reasons
         * idea is to create a ref per marker to link between each card and mark to bring the marker highlighted
         * into view on the left scroll for all estimate cards
         */
        () => pageDisplayRange.map(() => createRef()),
        [pageDisplayRange]
    );

    useEffect(() => {
        if (map.current) return; // initialize map only once
        initializeMap();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!map.current) return; // Waits for the map to initialize
        renderMarkersToMap(pageDisplayRange, allSortedProviders, markerRefs);

        // eslint-disable-next-line
    }, [page, highlightedProvider]);

    /**
     * Initializes mapbox Map with some properties
     */
    const initializeMap = () => {
        map.current = new mapboxgl.Map({
            // https://docs.mapbox.com/mapbox-gl-js/api/map/
            container: mapContainer.current,
            style: 'mapbox://styles/mapbox/streets-v11',
            center: [-73.9, 40.7], // this will later use the geolocation for what user searches in searchbar
            zoom: 12, // magic number, can be reconfigured later
        });
        map.current.addControl(new mapboxgl.NavigationControl());
        map.current.dragRotate.disable(); // disables google earth style angle zooming and rotations

        map.current.on('click', () => {
            // resets the highlighted provider to nothing
            setHighlightedProvider('');
        });
    };

    /**
     * Renders markers and fits the screen to fit all markers
     * @param {Array} pageDisplayRange - an array of scenarios
     * @param {Array} allProvidersPool - an array of providers
     */
    const renderMarkersToMap = (pageDisplayRange, allProvidersPool, markerRefs) => {
        resetMarkerData();
        if (pageDisplayRange?.length && allProvidersPool?.length) {
            const providerCoordinates = pageDisplayRange.map((_, index) => {
                let filteredProviderIndex = index + page * rowsPerPage;
                // ref for marker to pass to estimate card map view
                if (selectedProvider?.value) {
                    filteredProviderIndex = allProvidersPool.findIndex(
                        (prov) => prov.unTranslatedText == selectedProvider.value.unTranslatedText
                    );
                }

                const filteredProvider = allProvidersPool[filteredProviderIndex];

                displayMarker(filteredProvider, markerRefs[index]);
                return filteredProvider?.locAndPractRole?.length > 0
                    ? Object.values(filteredProvider?.locAndPractRole?.[0].position)
                    : [];
            });

            const bounds = calcBoundsFromCoordinates(providerCoordinates);
            map.current.fitBounds(bounds, { padding: 100 });
        }
    };

    /**
     * Resets the markers rendered on the map using the state variable currentMarkers
     */
    const resetMarkerData = () => {
        //reset the markers
        if (currentMarkers != []) {
            currentMarkers.forEach((marker) => {
                marker.remove();
            });
            setCurrentMarkers([]);
        }
    };

    /**
     * renders a representation of a provider on the map
     * @param {Object} provider - the provider information to be rendered
     * @param {Object} markerRef - the useRef to point to the marker to link on hover with estimate cards
     */
    const displayMarker = (provider, markerRef) => {
        const marker = createMarker(provider);
        // add marker to state to keep track for use in estimate cards
        setCurrentMarkers((currentMarkers) => [...currentMarkers, marker]);

        const markerDiv = marker.getElement();
        // if we applied the highlighted tag, then open the marker popUp
        if (markerDiv.getAttribute('aria-label') == 'marker-highlighted') {
            markerRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
                inline: 'nearest',
            });
            marker.togglePopup();
        }

        markerDiv.addEventListener('mouseenter', () => {
            setHighlightedProvider(provider.prefixId);
        });
    };

    /**
     * Creates the marker and attaches popUp
     * @param {Object} provider - the provider information to be rendered
     * @returns Marker Object
     */
    const createMarker = (provider) => {
        // creates the description/address for the popup
        const providerName = provider.unTranslatedText;
        /**
         * TODO for now we are using the first displayed location, want to eventually use current selected location
         * Ties into the other locations modal
         */
        const specialties =
            provider?.locAndPractRole?.[0]?.specialties?.length > 0
                ? provider?.locAndPractRole?.[0]?.specialties
                      ?.map((specialty) => {
                          return specialty;
                      })
                      .join(', ')
                : 'No Specialties Found';
        const phoneNum =
            provider?.locAndPractRole?.length > 0 &&
            provider?.locAndPractRole?.[0].phoneNums?.length > 0
                ? provider?.locAndPractRole?.[0]?.phoneNums[0]
                : 'defaultPhone';

        const popUp = (
            <LocationPopUp
                providerName={providerName}
                specialties={specialties}
                phoneNum={phoneNum}
            />
        );

        // creates an empty element to pass into mapbox Marker PopUp property
        // which then get replaces with the popUp component
        const popUpPlaceHolder = document.createElement('div');
        ReactDOM.render(popUp, popUpPlaceHolder);

        // empty element to pass to mapbox Marker constructor
        const el = document.createElement('div');
        el.className = 'marker';
        el.setAttribute(
            'aria-label',
            `${provider.prefixId === highlightedProvider ? 'marker-highlighted' : 'marker'}`
        );

        const lngLat =
            provider?.locAndPractRole?.length > 0
                ? Object.values(provider?.locAndPractRole?.[0].position)
                : [0, 0];
        const marker = new mapboxgl.Marker(el) // https://docs.mapbox.com/mapbox-gl-js/api/markers/
            .setLngLat(lngLat) // TODO make sure it maps correctly or if we want a dif idx of the arr
            .setPopup(
                new mapboxgl.Popup({ offset: [0, -20], closeButton: false }).setDOMContent(
                    popUpPlaceHolder
                )
            ) // https://docs.mapbox.com/mapbox-gl-js/api/markers/#popup
            .addTo(map.current);

        return marker;
    };

    /**
     * Returns the southwest most and northeast most coordinates
     * @param {Array} coordinatesCollection - an array of coordinates
     * @returns {Array} [SouthWest Coordinates, NorthEast Coordinates]
     */
    const calcBoundsFromCoordinates = (coordinatesCollection) => {
        return [getSWCoordinates(coordinatesCollection), getNECoordinates(coordinatesCollection)];
    };

    /**
     * Calculates the SouthWest most coordinates
     * @param {Array} coordinatesCollection - an array of coordinates
     */
    const getSWCoordinates = (coordinatesCollection) => {
        const lowestLng = Math.min(...coordinatesCollection.map((coordinates) => coordinates[0]));
        const lowestLat = Math.min(...coordinatesCollection.map((coordinates) => coordinates[1]));

        return [lowestLng, lowestLat];
    };

    /**
     * Calculates the NorthEast most coordinates
     * @param {Array} coordinatesCollection - an array of coordinates
     */
    const getNECoordinates = (coordinatesCollection) => {
        const highestLng = Math.max(...coordinatesCollection.map((coordinates) => coordinates[0]));
        const highestLat = Math.max(...coordinatesCollection.map((coordinates) => coordinates[1]));

        return [highestLng, highestLat];
    };

    /**
     * renders a single estimate (to the side of the map)
     * @param {Object} estimate
     * @param {Object} provider
     * @returns
     */
    const displayMapEstimate = (estimate, provider, markerRef) => {
        return (
            <MapEstimate
                estimate={estimate}
                setEstimate={setEstimate}
                provider={provider}
                setSingleEstimateResponse={setSingleEstimateResponse}
                setProvider={setProvider}
                key={`estimate-${estimate.id}`}
                otherLocationsOpen={otherLocationsOpen}
                setOtherLocationsOpen={setOtherLocationsOpen}
                closestLocationIndex={closestLocationIndex}
                setClosestLocationIndex={setClosestLocationIndex}
                topLocation={topLocation}
                setTopLocation={setTopLocation}
                highlightedProvider={highlightedProvider}
                setHighlightedProvider={setHighlightedProvider}
                markerRef={markerRef}
            />
        );
    };

    /**
     * renders all the estimates for providers within pageDisplayRange
     * @param {Array} allProvidersPool - an array of providers
     * @param {Array} markerRefs - an array of providers marker refrences
     */
    const displayMapEstimates = (allProvidersPool, markerRefs) => {
        const { sortedPageDisplayRange, sortedProviders } = sortEstimateWithProviders();

        allSortedProviders = sortedProviders;
        let estimateCardList = sortedPageDisplayRange.map((estimate, index) => {
            // when a single provider is selected
            if (selectedProvider?.value) {
                const filteredProviderIndex = allProvidersPool.findIndex(
                    (prov) => prov?.unTranslatedText == selectedProvider?.value?.unTranslatedText
                );
                return displayMapEstimate(
                    estimate,
                    allProvidersPool[filteredProviderIndex],
                    markerRefs[index]
                );
                // when displaying estimates for all the providers
            } else {
                return displayMapEstimate(
                    estimate,
                    sortedProviders[index + page * rowsPerPage],
                    markerRefs[index]
                );
            }
        });

        return estimateCardList;
    };

    return (
        <Grid container direction="row" wrap="nowrap">
            <Grid
                item
                xs={4}
                sx={{
                    minWidth: '400px',
                    paddingRight: '10px',
                    overflowY: 'scroll',
                    height: '880px',
                }}
            >
                {displayMapEstimates(allProvidersPool, markerRefs)}
            </Grid>
            <Grid
                item
                xs={8}
                ref={mapContainer}
                className="map-container"
                data-testid="mapContainer"
            />
        </Grid>
    );
};

export default MapView;
